import { Button, Icon, Modal, Select, Tooltip } from "@deligoo/ui"
import clsx from "clsx"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { toast } from "react-toastify"

import { useAppDispatch, useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { selectCount } from "@/store"
import { updateDriver, updateDriverSubteams } from "@/store/drivers"
import { selectActiveOrdersByDriverId, selectOrdersByDriverId } from "@/store/orders"
import { selectCurrentTeamStatsKey } from "@/store/teamsStats"
import { setDriverIdTasksRoute, setDriverIdTasksRouteHistory } from "@/store/ui"
import { Driver, Subteam } from "@/types"
import { fetchChangeTransportType, getIconFor } from "@/utils"

import cls from "../../DriverPanel.module.scss"
import { PaymentsSection } from "./PaymentsSection"

export const DetailsSection = ({ driver }: { driver: Driver }) => {
  const ordersCount = useAppSelector(selectCount(selectOrdersByDriverId(driver.id)))
  const activeOrdersCount = useAppSelector(selectCount(selectActiveOrdersByDriverId(driver.id)))
  const driverIdTasksRoute = useAppSelector((state) => state.ui.driverIdTasksRoute)
  const driverIdTasksRouteHistory = useAppSelector((state) => state.ui.driverIdTasksRouteHistory)

  const [isOpen, setIsOpen] = useState(false)

  const dispatch = useDispatch()

  return (
    <Card.Section className={clsx(cls.details, "hide-mobile")}>
      <div className={cls.header} onClick={() => setIsOpen((isOpen) => !isOpen)}>
        <h3 className="h200">Szczegóły</h3>
        <Icon name={isOpen ? "chevron-down" : "chevron-up"} size="small" color="muted" />
      </div>
      {isOpen && (
        <Card className={cls.expanded}>
          <Card.Section className={cls.routes}>
            <Button
              size="small"
              color="primary"
              className="px-2"
              iconProps={{ name: "crosshair" }}
              raised={Boolean(driverIdTasksRoute)}
              disabled={activeOrdersCount === 0}
              onClick={() => dispatch(setDriverIdTasksRoute(driverIdTasksRoute === driver.id ? null : driver.id))}
              fullWidth
            >
              Trasa
            </Button>

            <Button
              size="small"
              color="dark"
              className="px-2"
              iconProps={{ name: "crosshair" }}
              raised={Boolean(driverIdTasksRouteHistory)}
              disabled={ordersCount === 0}
              onClick={() =>
                dispatch(setDriverIdTasksRouteHistory(driverIdTasksRouteHistory === driver.id ? null : driver.id))
              }
              fullWidth
            >
              Historia
            </Button>
          </Card.Section>

          <SubteamsSection driverId={driver.id} subteams={driver.sub_teams} />

          <PaymentsSection driver={driver} />

          <TransportTypeSection driver={driver} />
        </Card>
      )}
    </Card.Section>
  )
}

const TransportTypeSection = ({ driver }: { driver: Driver }) => {
  const [isSelectShown, setIsSelectShown] = useState(false)
  const [selectedTransportType, setSelectedTransportType] = useState(driver.transport_type)
  const dispatch = useDispatch()
  const { t } = useTranslation("drivers")

  async function requestTransportTypeChange() {
    const toastSettings = {
      toastId: "changeDriverTransportType",
      updateId: "changeDriverTransportType",
    }

    try {
      const {
        response,
        json: { data: updatedDriver },
      } = await fetchChangeTransportType(driver.id, selectedTransportType)

      if (!response.ok) throw response

      if (updatedDriver) dispatch(updateDriver(updatedDriver))
      setIsSelectShown(false)
      toast.success(t("change_transport_type_success"), toastSettings)
    } catch (error) {
      toast.error(t("change_transport_type_fail"), toastSettings)
    }
  }

  return (
    <Card.Section className={clsx(cls.transportTypeSection)}>
      <h3 className="h200">Transport</h3>
      <div className={cls.transportTypeWrapper}>
        {isSelectShown ? (
          <>
            <Select
              wrapperClassName={cls.selectWrapper}
              inputSize="small"
              className={cls.select}
              name="transport_type"
              id="transport_type"
              value={selectedTransportType}
              onChange={(e) => setSelectedTransportType(e.currentTarget.value as Driver["transport_type"])}
            >
              <option value="car">{t("car")}</option>
              <option value="bicycle">{t("bicycle")}</option>
              <option value="scooter">{t("scooter")}</option>
            </Select>
            <Button className="ml-2" color="primary" size="small" onClick={requestTransportTypeChange}>
              Zapisz
            </Button>
          </>
        ) : (
          <>
            <div className={cls.transportType}>
              <div className={cls.icon}>
                <Icon name={getIconFor(driver.transport_type)} color="primary" />
              </div>
              <span className="font-weight-bold">{t(driver.transport_type)}</span>
            </div>

            <Button color="primary" size="small" onClick={() => setIsSelectShown(true)}>
              Zmień
            </Button>
          </>
        )}
      </div>
    </Card.Section>
  )
}

const SubteamsSection = ({ driverId, subteams }: { driverId: Driver["id"]; subteams: Subteam[] }) => {
  const [isDriverSubteamsModalActive, setDriverSubteamsModalActive] = useState(false)

  return (
    <section className={cls.subteams}>
      <header className="d-flex align-center justify-between mb-2">
        <div className="text-desc text-muted">Podzespoły</div>

        <Tooltip content="Edytuj podzespoły" placement="left">
          <span>
            <Button
              color="primary"
              variation="outlined"
              size="small"
              iconProps={{ name: "edit" }}
              onClick={() => setDriverSubteamsModalActive(true)}
            />
          </span>
        </Tooltip>
      </header>

      <div className="text-medium">{subteams.map((subteam) => subteam.name).join(", ")}</div>

      {isDriverSubteamsModalActive && (
        <DriverSubteamsModal
          driverId={driverId}
          driverSubteams={subteams}
          handleClose={() => setDriverSubteamsModalActive(false)}
        />
      )}
    </section>
  )
}

type DriverSubteamsModalProps = {
  driverId: Driver["id"]
  driverSubteams: Subteam[]
  handleClose: () => void
}

const DriverSubteamsModal = ({ driverId, driverSubteams, handleClose }: DriverSubteamsModalProps) => {
  const currentTeamSubteams = useAppSelector(selectCurrentTeamStatsKey("sub_teams"))

  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useAppDispatch()

  function updateSubteams(subteamToUpdateId: Subteam["id"]) {
    const updatedSubteamIds = driverSubteams.map((subteam) => subteam.id)
    const subteamToUpdateIndex = updatedSubteamIds.indexOf(subteamToUpdateId)

    if (subteamToUpdateIndex === -1) {
      updatedSubteamIds.push(subteamToUpdateId)
    } else {
      updatedSubteamIds.splice(subteamToUpdateIndex, 1)
    }

    try {
      setIsLoading(true)
      dispatch(updateDriverSubteams({ driverId, sub_team_ids: updatedSubteamIds }))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal className={cls.driverSubteamsModal} handleClose={handleClose} closeBtnPosition="inside">
      <Modal.Title>Zarządzaj podzespołami</Modal.Title>

      <Modal.Content className={cls.subteamsButtons}>
        {currentTeamSubteams?.map((subteam) => (
          <Button
            onClick={() => updateSubteams(subteam.id)}
            color={driverSubteams.find((driverSubteam) => driverSubteam.id === subteam.id) ? "primary" : undefined}
            loading={isLoading}
            key={subteam.id}
            size="large"
            fullWidth
          >
            {subteam.name}
          </Button>
        ))}
      </Modal.Content>
    </Modal>
  )
}
